import type { DiffAstComment } from "@ext/git/core/Diff/logic/commentsDiff/CommentsDiff";
import matchUnchangedBlocks from "@ext/git/core/Diff/logic/matchUnchangedBlocks";
import type { CommentDiffLine, Pos } from "@ext/git/core/Diff/logic/model/DiffLine";
import { FileStatus } from "@ext/Watchers/model/FileStatus";
import type { DiffChange } from "@gaurussel/tiptap-diff-utility";
import type { Node } from "prosemirror-model";

// Clamped so `to + 1` stays inside the doc: `coordsAtPos` consumes it.
const blockBounds = (node: Node, pos: number): Pos => ({ from: pos + 1, to: pos + node.nodeSize - 2 });

const blockAt = (doc: Node, pos: number): { node: Node; pos: number } | undefined => {
	const resolved = doc.resolve(Math.min(Math.max(pos, 0), doc.content.size));

	for (let depth = resolved.depth; depth > 0; depth--) {
		const node = resolved.node(depth);
		if (node.isTextblock) return { node, pos: resolved.before(depth) };
	}

	return undefined;
};

const getCommentDiffLines = (
	comments: DiffAstComment[],
	before: Node,
	after: Node,
	changes: DiffChange[],
): CommentDiffLine[] => {
	if (!comments.length) return [];

	const lines: CommentDiffLine[] = [];

	const add = (pos: Pos, comment: DiffAstComment) => {
		const existing = lines.find((line) => line.pos.from === pos.from && line.pos.to === pos.to);
		if (existing) existing.comments.push(comment);
		else lines.push({ type: "comment", pos, comments: [comment] });
	};

	const hasDeleted = comments.some((comment) => comment.type === FileStatus.delete);
	const pairs = hasDeleted ? matchUnchangedBlocks(changes, before, after) : [];

	const movedOrModified = changes.filter((change) => change.type === "modified" || change.type === "moved");

	comments.forEach((comment) => {
		if (comment.type !== FileStatus.delete) {
			const block = blockAt(after, comment.pos.from);
			if (block) add(blockBounds(block.node, block.pos), comment);
			return;
		}

		const oldBlock = blockAt(before, comment.oldPos.from);
		if (!oldBlock) return;

		const change = movedOrModified.find((candidate) => candidate.posA === oldBlock.pos);
		if (change?.nodeB && change.posB !== undefined) {
			add(blockBounds(change.nodeB, change.posB), comment);
			return;
		}

		const pair = pairs.find((candidate) => candidate.posA === oldBlock.pos);
		if (pair) add(blockBounds(pair.nodeB, pair.posB), comment);
	});

	return lines;
};

export default getCommentDiffLines;
